<template>
  <TourFilterPopupWrapper
    ref="calendarPopup"
    filter-key="date"
    :title
    :static-title="staticTitle"
    :is-active="isActive"
    :is-disabled-reset-button="isDisabledResetButton"
    is-modal-only
    modal-no-padding
    modal-class="filter-dates-modal with-chips"
    class="filter-dates"
    @open="onPopupOpen"
    @apply="handleApply"
  >
    <div class="filter-weekdays ref-modal-fixed">
      <CalendarWeekdays />
    </div>

    <Calendar
      ref="calendarRef"
      :key="calendarKey"
      :default-selected-dates="calendarDefaultDates"
      no-weekdays
      class="filter-calendar"
      @select="onCalendarSelect"
    />

    <template #footer>
      <UiChips
        :options="displayChips"
        class="dates-chips"
        :modelValue="activeDateChip"
        emit-value
        small
        @update:modelValue="onServerChipSelect"
      />
    </template>
  </TourFilterPopupWrapper>
</template>

<script setup lang="ts">
  import { TourFilterPopupWrapper } from '@/entities/filter/popup-wrapper';
  import { useTourFiltersStore, type TTourFiltersState } from '@/entities/filter/store/tour';
  import { Calendar, CalendarWeekdays } from '@experience/core-calendar-ui';
  import { useI18n } from '@experience/i18n';
  import { DEFAULT_FILTER_TITLE } from '../../const/config';
  import ru from '../../i18n/ru.json';
  import { toCalendarSelectedDate } from '../../lib/localDate';
  import { getFilterDate, getFilterTitle } from '../../lib/title';
  import { UiChips, type ChipOption, type UiChipsProps } from '@experience/core-chips-ui';
  import { computed, h, type useShadowRoot } from 'vue';
  import { ConfettyIcon } from '@/shared/ui/icons/confettyIcon';
  import type { GetCalendarEventChipsResponse, GetCalendarEventsChipsResponse } from '@/shared/api/experience';
  import { useFiltersDateRange } from '@/features/filter/dates/composables/useFiltersDateRange.ts';

  const { t } = useI18n({
    inheritLocale: true,
    messages: {
      ru,
    },
  });

  const activeDateChip = ref<ChipOption | null>(null);
  type ChipsModelValue = UiChipsProps['modelValue'];
  const rootNode = inject<ReturnType<typeof useShadowRoot>>('shadowRoot', null);
  const { fastDateRangeRef, lastAppliedFastDateRangeRef } = useFiltersDateRange();

  interface Props {
    slug: string;
    calendarEvents?: GetCalendarEventsChipsResponse;
  }

  const props = defineProps<Props>();

  interface Emits {
    (e: 'apply', filterData?: Partial<TTourFiltersState>): void;
  }

  const emit = defineEmits<Emits>();

  const filtersStore = useTourFiltersStore();
  // TODO: вынести тип наерх
  const calendarRef = ref<
    | (InstanceType<typeof Calendar> & {
        scrollToMonth: (year: number, month: number) => void;
      })
    | null
  >(null);

  const isSmallScreen = ref(false);
  const mql = window.matchMedia('(max-width: 360px)');

  const staticTitle = computed(() => {
    return t('title');
  });

  const updateMatch = () => {
    isSmallScreen.value = mql.matches;
  };

  const allPeriods = computed((): GetCalendarEventChipsResponse[] => [
    ...(props.calendarEvents?.public_holidays ?? []),
    ...(props.calendarEvents?.months ?? []),
    ...(props.calendarEvents?.seasons ?? []),
  ]);

  const holidayValues = computed<Set<string>>(
    () =>
      new Set(
        (props.calendarEvents?.public_holidays ?? []).map(({ start_date, end_date }) => `${start_date}_${end_date}`),
      ),
  );

  const formattedDateChips = computed<ChipOption[]>(() =>
    allPeriods.value.map((item) => {
      const val = `${item.start_date}_${item.end_date}`;

      const isHoliday = holidayValues.value.has(val);

      return {
        label: isHoliday ? getFilterDate(item.start_date, item.end_date) : item.name,
        value: val,
        icon: isHoliday ? h(ConfettyIcon) : undefined,
      };
    }),
  );

  const displayChips = computed(() =>
    isSmallScreen.value ? formattedDateChips.value.slice(0, 6) : formattedDateChips.value,
  );

  watch(
    () => [filtersStore.state.date.from, filtersStore.state.date.to],
    ([from, to]) => {
      const match = formattedDateChips.value.find((chip) => {
        const [chipFrom, chipTo] = String(chip.value).split('_');

        return chipFrom === from && chipTo === to;
      });

      activeDateChip.value = match ?? null;
    },
  );

  // TODO: вынести в composable
  const onServerChipSelect = async (value: ChipsModelValue) => {
    if (!value) {
      activeDateChip.value = null;
      filtersStore.state.date.from = null;
      filtersStore.state.date.to = null;
      fastDateRangeRef.value = null;
      defaultDates.value = [filtersStore.state.date.from, filtersStore.state.date.to];
      calendarKey.value += 1;

      return;
    }

    const str = Array.isArray(value) ? (value[0] as string) : (value as string);
    const [fromIso, toIso] = str.split('_');

    filtersStore.state.date.from = fromIso;
    filtersStore.state.date.to = toIso;
    fastDateRangeRef.value = str;
    defaultDates.value = [filtersStore.state.date.from, filtersStore.state.date.to];
    calendarKey.value += 1;

    await nextTick();

    const { year, monthIndex } = parseYearMonth(fromIso);
    const today = new Date();
    if (!(year === today.getFullYear() && monthIndex === today.getMonth())) {
      calendarRef.value?.scrollToMonth(year, monthIndex);
    } else {
      const modal = rootNode?.querySelector<HTMLElement>('.filter-dates-modal');
      if (!modal) {
        return;
      }

      const scrollable = modal.querySelector<HTMLElement>('.ui-modal__scroll-container') ?? modal;

      scrollable.scrollTo({ top: 0, behavior: 'smooth' });
    }

    activeDateChip.value = formattedDateChips.value.find((chip) => chip.value === str) ?? null;
  };

  const calendarPopup = ref(null);

  const title = ref(DEFAULT_FILTER_TITLE);
  const calendarKey = ref(1);
  const defaultDates = ref<[Nullable<string>, Nullable<string>] | undefined>([
    filtersStore.searchState.date.from ?? '',
    filtersStore.searchState.date.to ?? '',
  ]);

  const calendarDefaultDates = computed(() => defaultDates.value?.map(toCalendarSelectedDate));

  function onCalendarSelect(from: string | null, to: string | null) {
    filtersStore.state.date.from = from;
    filtersStore.state.date.to = to;
    fastDateRangeRef.value = null;
  }

  const isDisabledResetButton = computed(() => {
    return !filtersStore.state.date.from && !filtersStore.state.date.to;
  });

  const isActive = computed(() => title.value !== DEFAULT_FILTER_TITLE);

  watchEffect(() => {
    title.value = getFilterTitle(filtersStore.searchState.date.from, filtersStore.searchState.date.to);
  });

  watch(
    () => filtersStore.state.date,
    () => {
      defaultDates.value = [filtersStore.state.date.from ?? '', filtersStore.state.date.to ?? ''];
    },
  );

  watch(
    () => activeDateChip.value,
    (chip) => {
      if (chip?.value) {
        lastAppliedFastDateRangeRef.value = String(chip.value);
      }
    },
    { immediate: true },
  );

  const parseYearMonth = (iso: string) => {
    const [yearStr, monthStr] = iso.split('-');

    return { year: Number(yearStr), monthIndex: Number(monthStr) - 1 };
  };

  // TODO: вынести в composable
  const updateActiveChipAndScroll = () => {
    const from = filtersStore.state.date.from;
    const to = filtersStore.state.date.to;
    if (!from) {
      return;
    }

    const val = `${from}_${to}`;
    activeDateChip.value = formattedDateChips.value.find((chip) => chip.value === val) ?? null;

    const { year, monthIndex } = parseYearMonth(from);
    const today = new Date();
    if (year === today.getFullYear() && monthIndex === today.getMonth()) {
      return;
    }
    setTimeout(() => {
      calendarRef.value?.scrollToMonth(year, monthIndex);
    }, 300);
  };

  const onPopupOpen = async () => {
    updateActiveChipAndScroll();
  };

  const handleApply = () => {
    const filterData: Partial<TTourFiltersState> = {
      date: filtersStore.searchState.date,
    };

    emit('apply', filterData);
  };

  onMounted(() => {
    mql.addEventListener('change', updateMatch);
    updateMatch();
  });

  onBeforeUnmount(() => {
    mql.removeEventListener('change', updateMatch);
  });
</script>

<style scoped lang="scss">
  @use '@experience/shared/dist/assets/adaptivity' as adaptivity;

  .filter-dates {
    :deep(.infinite-calendar) {
      height: 100%;
    }

    @include adaptivity.mobile {
      :deep(.ui-chip) {
        justify-content: space-between;
        width: 100%;
        max-width: 100%;
      }

      :deep(.ui-chip__label) {
        text-align: left;
      }

      :deep(.filter-popup) {
        margin-top: 32px;
        border-top-left-radius: 16px;
        border-top-right-radius: 16px;
      }
    }
  }
</style>

<style lang="scss">
  @use '@experience/shared/dist/assets/adaptivity' as adaptivity;
  @use '@experience/design-tokens/typography' as typography;

  .filter-dates-modal {
    .ui-modal-header_fixed {
      &:after {
        display: none;
      }
    }

    .filter-weekdays {
      --cell-height: 32px;
      position: relative;
      display: flex;
      justify-content: center;
      background-color: var(--alias-color-background-default);

      &:after {
        content: ' ';
        position: absolute;
        bottom: 0;
        left: 0;
        width: 100%;
        height: 1px;
        background-color: var(--alias-color-border-default);
      }
    }

    .dates-chips {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
      max-height: none;
      padding: 8px 0 16px;
    }

    &.with-chips {
      .ui-modal-footer {
        padding-top: 8px;

        @include adaptivity.tablet {
          padding-top: 0;
        }
      }
    }
  }

  .experiment-block,
  .experiment-block-date-title {
    padding: 8px 16px;
  }

  .experiment-block-date-title {
    @include typography.base-500-400-semi;
  }
</style>
